import { useCallback, useState } from 'react';
import { PrefSegmentedControl, PrefSwitch } from '../components/PrefControls';
import {
  readAutoHideRecordDiscussionPanel,
  readRecordDiscussionPlacement,
  writeAutoHideRecordDiscussionPanel,
  writeRecordDiscussionPlacement,
  type RecordDiscussionPlacement
} from '../patientTracking/discussion/recordDiscussionPrefs';
import '../theme/themePreferences.css';

const PLACEMENT_ITEMS: { text: string; value: RecordDiscussionPlacement }[] = [
  { text: 'Left side', value: 'left' },
  { text: 'Right side', value: 'right' },
  { text: 'Off', value: 'off' }
];

export function RecordDiscussionPreferencesPanel() {
  const [placement, setPlacement] = useState<RecordDiscussionPlacement>(() =>
    readRecordDiscussionPlacement()
  );
  const [autoHide, setAutoHide] = useState(() => readAutoHideRecordDiscussionPanel());

  const onPlacementChanged = useCallback((value: RecordDiscussionPlacement) => {
    setPlacement(value);
    writeRecordDiscussionPlacement(value);
  }, []);

  const onAutoHideChanged = useCallback((value: boolean) => {
    setAutoHide(value);
    writeAutoHideRecordDiscussionPanel(value);
  }, []);

  return (
    <section className="theme-preferences" aria-labelledby="record-discussion-preferences-heading">
      <h3 id="record-discussion-preferences-heading" className="theme-preferences__title">
        Record discussions
      </h3>
      <p className="theme-preferences__hint">
        Choose whether and where the discussion panel appears when you open a patient, covered entity, or
        tracker issue from the pharmacy grids.
      </p>
      <div className="theme-preferences__section">
        <span className="theme-preferences__label">Panel position</span>
        <PrefSegmentedControl
          className="theme-preferences__family-group"
          items={PLACEMENT_ITEMS}
          value={placement}
          aria-label="Panel position"
          onChange={onPlacementChanged}
        />
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          When set to Off, the record form uses the full width. You can still change placement from the
          settings menu in the record popup title bar.
        </p>
      </div>
      <div className="theme-preferences__section">
        <div className="theme-preferences__compact-row">
          <PrefSwitch
            checked={autoHide}
            disabled={placement === 'off'}
            onChange={onAutoHideChanged}
            aria-label="Auto-hide discussion panel"
          />
          <span className="theme-preferences__hint theme-preferences__hint--inline">
            Auto-hide discussion panel
          </span>
        </div>
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          When enabled, the discussion panel stays collapsed until you move the pointer over the left or right
          edge (matching your panel position) or expand it from the record popup title bar.
        </p>
      </div>
    </section>
  );
}
